import { Link, useParams } from "react-router-dom";
import { getActorById, getDramasByIds } from "@/lib/data";
import PageHeader from "@/components/base/PageHeader";
import EmptyState from "@/components/base/EmptyState";
import DramaCard from "@/components/base/DramaCard";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function ActorDetail() {
  const { id } = useParams<{ id: string }>();
  const actor = id ? getActorById(id) : undefined;

  usePageMeta(
    actor ? `${actor.name} 演员介绍 - 漂亮的保姆` : "演员介绍 - 漂亮的保姆",
    actor
      ? `${actor.name}，${actor.region}演员，出生于 ${actor.birth}。在漂亮的保姆查看个人简介、出演作品与相关剧集信息。`
      : "在漂亮的保姆查看演员个人简介与出演作品。",
    actor ? `${actor.name},演员介绍,${actor.region},作品列表,漂亮的保姆` : "演员介绍,漂亮的保姆",
  );

  if (!actor) {
    return (
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-16">
        <EmptyState
          title="未找到该演员"
          desc="你访问的演员资料不存在，可以返回演员列表浏览其他人。"
          actionText="返回演员介绍"
          onAction={() => window.REACT_APP_NAVIGATE("/actors")}
        />
      </div>
    );
  }

  const works = getDramasByIds(actor.works);

  return (
    <>
      <PageHeader
        title={`${actor.name} · 演员介绍`}
        crumbs={[{ label: "首页", to: "/" }, { label: "演员介绍", to: "/actors" }, { label: actor.name }]}
      />

      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
        <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-6 grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-6">
          <div className="w-48 mx-auto sm:mx-0 sm:w-full">
            <div className="aspect-square rounded-lg overflow-hidden bg-background-200 border border-background-200">
              <img
                src={actor.avatar}
                alt={actor.name}
                title={`${actor.name} 演员照片 - 漂亮的保姆`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <div className="min-w-0">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              {actor.name}
            </h1>
            <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div className="flex gap-2">
                <dt className="text-foreground-400 shrink-0">出生日期</dt>
                <dd className="text-foreground-800">{actor.birth}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-400 shrink-0">国籍 / 地区</dt>
                <dd className="text-foreground-800">{actor.region}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-400 shrink-0">身高</dt>
                <dd className="text-foreground-800">{actor.height}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-400 shrink-0">代表作品</dt>
                <dd className="text-foreground-800">{actor.works.length} 部</dd>
              </div>
            </dl>
            <div className="mt-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                个人简介
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-loose">
                {actor.bio}
              </p>
            </div>
          </div>
        </div>

        <section className="mt-8">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            <h2 className="font-heading text-xl font-bold text-foreground-950">
              主要作品
            </h2>
          </div>
          {works.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {works.map((d) => (
                <DramaCard key={d.id} drama={d} />
              ))}
            </div>
          ) : (
            <EmptyState title="暂无作品信息" />
          )}
        </section>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/actors"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-background-50 border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            返回演员列表
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-5-line" />
            返回首页
          </Link>
        </div>
      </div>
    </>
  );
}